import React from "react";
import {
  empireEngineeringVerticalLogo,
  imgCompany1,
  imgService3,
} from "../../assets/imgExport";
import GetInTouch from "../../components/GetInTouch/GetInTouch";
import {
  IconEngineering,
  IconHandshake,
  IconStrategy,
} from "../../assets/svgExport";

export default function AboutUsPage() {
  function HeroSection() {
    return (
      <div className="hero-container">
        <p className="subtitle">Who We Are</p>
        <h1>
          Connecting Europe’s offshore wind ambitions with Asia’s vessel
          capabilities.
        </h1>
        <p className="paragraph">
          Venti Maris is a UK-based consultancy and service provider, helping
          European developers and contractors access Asia’s cost-effective heavy
          transport fleet with the assurance, precision and professionalism
          expected from European project delivery.
        </p>
      </div>
    );
  }

  function WhyWeExistSection() {
    return (
      <div className="equal-flex">
        <div>
          <p className="subtitle">Why We Exist</p>
          <h2>
            Transforming offshore wind logistics through global collaboration
          </h2>
          <div>
            <p>
              Twenty years of offshore wind delivery proved that when developers
              can access global vessel capacity, they deliver ambitious projects
              both on time and within budget.
            </p>
            <p>
              We support offshore wind expansion by connecting international
              maritime resources. Our approach aims to break down geographical
              barriers to accelerate renewable energy development.
            </p>
          </div>
          <div className="flex gap-10">
            <div>
              <p className="font-bold text-base">Our mission</p>
              <p>
                To consistently deliver safe, engineered, cost-effective, and
                environmentally responsible marine transport services,
                empowering clients to achieve successful offshore wind projects
                globally.
              </p>
            </div>
            <div>
              <p className="font-bold text-base">Our vision</p>
              <p>
                To lead the maritime logistics industry by enabling seamless
                offshore wind energy expansion worldwide through innovative,
                reliable, and sustainable transportation solutions.
              </p>
            </div>
          </div>
        </div>
        <img src={imgService3} alt="wind-farm-shipping" />
      </div>
    );
  }

  function OurDifferenceSection() {
    return (
      <div className="text-center">
        <p className="subtitle">Our Difference</p>
        <h2>What sets Venti Maris apart</h2>
        <p className="max-w-[70%] mx-auto">
          We understand the complex landscape of offshore wind logistics and
          deliver tailored solutions that exceed industry standards. Success in
          this industry comes down to experience and trust. Our team of senior
          people combines the exact expertise needed.
        </p>
        <div className="flex gap-10 flex-col md:flex-row mt-20">
          <div className="flex flex-col items-center">
            <IconStrategy className="w-10 h-10 mb-4" />
            <h3>Developer focus</h3>
            <p>
              Strategies built around your specific project requirements and
              financial objectives. Our team comprises industry specialists who
              blend technical expertise with deep maritime logistics experience.
            </p>
          </div>
          <div className="flex flex-col items-center">
            <IconEngineering className="w-10 h-10 mb-4" />
            <h3>Engineering excellence</h3>
            <p>
              Supported by our partner, Empire Engineering, we offer integrated
              engineering and project management solutions that bridge
              traditional shipbroking with complete project execution.
            </p>
          </div>
          <div className="flex flex-col items-center">
            <IconHandshake className="w-10 h-10 mb-4" />
            <h3>Asian maritime network</h3>
            <p>
              We do more than simply finding vessels, we provide a complete
              solution that makes Asian vessels as accessible and reliable as
              European alternatives.
            </p>
          </div>
        </div>
      </div>
    );
  }

  function OurApproachToYourProjectSection() {
    return (
      <div className="equal-flex">
        <div>
          <p className="subtitle">Our Approach to Your Project</p>
          <h2>How We Work</h2>
          <p>
            Every project is unique, so our involvement flexes to match your
            needs. We can support a single phase such as chartering or cargo
            engineering or manage the entire transport scope from supporting
            through tender all the way to final execution.
          </p>
          <p>Our approach is defined by:</p>
          <ul className="list-disc list-inside flex flex-col gap-3">
            <li>Early-stage planning to identify risks and opportunities.</li>
            <li>
              Integrated engineering and logistics design to reduce interfaces.
            </li>
            <li>Clear communication and reporting throughout execution.</li>
          </ul>
        </div>
        <img src={imgCompany1} alt="a team of people working on a project" />
      </div>
    );
  }

  function PartnershipSection() {
    return (
      <div className="equal-flex">
        <div className="order-first lg:order-last">
          <p className="subtitle">Partnership</p>
          <h2>Our Partnership with Empire Engineering</h2>
          <p>
            <a
              className="text-main-light text-inherit-size underline"
              href="https://www.empireengineering.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Empire Engineering
            </a>{" "}
            provides the technical backbone of our service, delivering advanced
            transport engineering and expertise.
          </p>
          <p>
            From seafastening design, stability analysis and loadout
            engineering, to cargo fatigue life assessment and transportation
            impact analysis, our engineering team is there to provide solutions
            for the entire shipping journey.
          </p>
          <p>
            Together, we offer complete peace of mind that every component will
            arrives as expected.
          </p>
          <div className="flex gap-10">
            <div>
              <p className="font-bold text-base">Advanced engineering</p>
              <p>
                Comprehensive transport engineering and Marine Warranty-level
                technical support.
              </p>
            </div>
            <div>
              <p className="font-bold text-base">One less tender</p>
              <p>
                One less interface to coordinate and the confidence that your
                cargo will arrive ready for installation.
              </p>
            </div>
          </div>
        </div>
        <div className="px-36 md:p-36 max-w-[480px] mx-auto">
          <img
            src={empireEngineeringVerticalLogo}
            alt="Empire Engineering logo"
            style={{ objectFit: "contain" }}
          />
        </div>
      </div>
    );
  }

  function OurCommitmentSection() {
    return (
      <div className="equal-flex">
        <div>
          <h2>Our Commitment</h2>
          <p>
            At Venti Maris, safety, precision engineering, sustainability, and
            technological innovation drive our business. Every transport
            operation is carefully planned and executed, adhering strictly to
            international safety standards and regulatory compliance, ensuring
            reliable and secure delivery every time.
          </p>
          <p>
            We deliver tailored solutions that prioritise efficiency, cost-
            effectiveness, and environmental responsibility, helping our clients
            reduce logistical complexities and optimize their project outcomes.
          </p>
        </div>
        <div></div>
      </div>
    );
  }

  return (
    <div>
      <HeroSection />
      <div className="body-container">
        <WhyWeExistSection />
        <OurDifferenceSection />
        <OurApproachToYourProjectSection />
        <PartnershipSection />
        <OurCommitmentSection />
        <GetInTouch />
      </div>
    </div>
  );
}
